import { computed, ref, watchEffect } from "vue";
import person from "@/assets/images/svg/person.svg";
import printer from "@/assets/images/svg/printer.svg";
import BookingModule from "@/store/modules/booking";
import {
  BookingStatus,
  IProductSlotState,
  IQuantifiedProductSlotState,
  isSeatedPssType,
  PaymentStatus,
  PSSType,
} from "@/types/db/product-slot-state";
import { formatDateTime } from "@/utils/helpers/dates";
import { BookingStep } from "@/constants/booking";
import CustomersModule from "@/store/modules/customers";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { useI18n } from "vue-i18n";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import { refreshDiscount } from "@/utils/vue-helpers/discount";
import {
  groupedPSS,
  deleteUnconfirmedTickets,
  clearBookingCustomFields,
  printBookingReceipt,
} from "@/utils/vue-helpers/booking";
import { sumArray } from "@/utils/helpers/common";
import calculatorSvg from "@/assets/images/svg/calculator-sign.svg";
import { cloneDeep, sum } from "lodash";
import {
  createCancelPSSAccounting,
  createCancelPSSDiscountAccounting,
} from "@/utils/vue-helpers/accounting";
import { LogService } from "@/services/logging";
import dayjs from "dayjs";
import { IProductCategory, PCTypes } from "@/types/db/product-category";
import { assignOrEditCustomer } from "@/utils/vue-helpers/customer";
import {
  bookingTotalConsideringDiscounts,
  bookingTotalTax,
  discountTotal,
  paymentAndDiscountTotal,
  restToPay,
  restToPayForInvoice,
  getPssGroupDiscount,
} from "@/utils/vue-helpers/new-accountings";
import { IAccountingEntry } from "@/types/db/accounting-entry";
import POSConfigModule from "@/store/modules/pos-config";
import CashFloatModule from "@/store/modules/cash-float";
import { Currency } from "@/constants/currency";
import {
  getPSSValue,
  getPackageTotalValue,
} from "@/utils/helpers/product-slot-state";
import { getProductCategoryByPriceId } from "@/dexie/product-category";
import NotificationService from "@/services/notification.service";
import { openInternetNeededMessageBox } from "@/utils/vue-helpers/common";
import ExternalVouchersModule from "@/store/modules/external-vouchers";
import { createCancelPSSRefundIncertAccounting } from "@/api/external-vouchers/incert";
import { IExternalVoucher } from "@/interfaces/store/IExternalVouchersState";
import {
  applyOffersToBooking,
  isOfferAccountingRefunded,
} from "@/utils/helpers/offers";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import OffersModule from "@/store/modules/offers";
import {
  getUntypedBookingByUuid,
  isBookingInDexie,
} from "@/utils/helpers/booking";
import { PaymentMethodTypes } from "@/types/db/pos-config";
import AppModule from "@/store/modules/app";
import { provide } from "vue";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import { getLinkedWaiversCount } from "@/api/waivers/waivers";
import Timeout from "await-timeout";

/**
 * Returns all necessary variables (objects and functions) used in AsideOrderSummary and can be used as well in CustomerBasket
 */
export function setupAsideOrderSummary() {
  const showCancelTicketsModal = ref(false);
  const isOnline = ref(window.navigator.onLine);
  const iconStyle =
    "width: 18px; height: 18px; filter: brightness(0) saturate(100%) contrast(170%) invert(100%);";
  const iconContent = person;
  const iconAlt = "user";

  const printIcon = printer;
  const printAlt = "print";

  const calcIcon = calculatorSvg;
  const calcAlt = "calculator";
  const calcIconStyle = "width: 30px; height: 30px;";

  const { t } = useI18n({});
  const calcVisibility = ref(false);

  const bookings = ref(BookingModule.bookings);
  const bookingList = ref(BookingModule.bookingList);

  const confirmedPSSToDelete = ref<IProductSlotState[]>([]);
  // computed property used to show removeButton loading if the removal process involves async operations
  const isRemoveButtonLoading = ref(false);
  const showCancelConfirmationModal = ref(false);
  const cancelConfirmationPss = ref<IProductSlotState[]>([]);
  const selectedConfirmedPssUuids = ref<string[]>([]);

  const cancelConfirmationCount = computed(() => {
    return cancelConfirmationPss.value.length;
  });

  const selectedConfirmedPssCount = computed(() => {
    return selectedConfirmedPssUuids.value.length;
  });

  const isAllConfirmedPssSelected = computed(() => {
    if (!confirmedPSSToDelete.value.length) return false;
    return (
      selectedConfirmedPssUuids.value.length ===
      confirmedPSSToDelete.value.length
    );
  });

  const groupedPSSList = computed(() => {
    const gr = groupedPSS(
      BookingModule.selectedBooking ? BookingModule.selectedBooking.pssList : []
    );
    return gr;
  });

  const selectedBooking = computed(() => {
    const b = BookingModule.selectedBooking;
    return b;
  });

  const currency = computed(() => {
    return BookingModule.selectedBooking?.currency;
  });

  const pssList = ref(BookingModule.selectedBooking?.pssList);

  const showCopyCheck = ref(false);

  const bookingHasSeatingPlan = computed(() => {
    const confirmedPssList = pssList.value?.filter(
      (ps) => ps.bookingStatus === BookingStatus.Confirmed
    );
    if (!confirmedPssList || !confirmedPssList.length) return false;

    return confirmedPssList.some((ps) => isSeatedPssType(ps.type));
  });

  const bookingStep = computed(() => {
    const b = BookingModule.bookingStep;
    return b;
  });

  const isInOrderStep = computed(() => {
    const b = BookingModule.bookingStep === BookingStep.Order;
    return b;
  });

  const isInPaymentStep = computed(() => {
    const b =
      bookingStep.value === BookingStep.Payment ||
      bookingStep.value === BookingStep.Discount ||
      bookingStep.value === BookingStep.Promocode ||
      bookingStep.value === BookingStep.PromocodeCampaigns;
    bookingStep.value === BookingStep.Offers;
    return b;
  });

  /**
   * Whether the booking requires a waiver.
   * TODO: replace this with a real check once the backend
   * supplies waiver-required info on the booking or product.
   * For now we always show the waiver section in payment step.
   */
  const bookingRequiresWaiver = computed(() => {
    // TODO: e.g. return BookingModule.selectedBooking?.requiresWaiver ?? false
    return true;
  });

  /**
   * Show the waiver section only in the payment step
   * and when the booking requires a waiver.
   */
  const showWaiverSection = computed(() => {
    return isInPaymentStep.value && bookingRequiresWaiver.value;
  });

  const linkedWaivers = computed(() => {
    return BookingModule.selectedBookingLinkedWaivers;
  });
  // ── Waiver linked count ───────────────────────────────────────────────────
  const linkedCount = ref(0);
  const isLoadingCount = ref(false);

  async function fetchLinkedWaiversCount() {
    isLoadingCount.value = true;
    try {
      const uuid = BookingModule.selectedBookingUUID;
      let bookingInfo = null;
      let retries = 0;
      while (!bookingInfo && retries < 3) {
        bookingInfo = await getBookingWithUUID(uuid);
        if (!bookingInfo) {
          retries++;
          await Timeout.set(3000);
        }
      }
      if (!bookingInfo?.id) return;
      linkedCount.value = await getLinkedWaiversCount(bookingInfo.id);
    } catch (e) {
      NotificationService.showGeneralResponseError();
    } finally {
      isLoadingCount.value = false;
    }
  }

  function incrementLinkedCount() {
    linkedCount.value++;
  }

  function decrementLinkedCount() {
    if (linkedCount.value > 0) linkedCount.value--;
  }

  // Fetch count when booking reaches CompleteOrder step
  watchEffect(() => {
    if (BookingModule.bookingStep === BookingStep.CompleteOrder) {
      fetchLinkedWaiversCount();
    }
  });

  const isCompletedBooking = computed(() => {
    const b = BookingModule.bookingStep === BookingStep.CompleteOrder;
    return b;
  });

  const isWaiverAllowed = computed(() => {
    const b =
      AppModule.debugMode ||
      POSConfigModule.groupId === 1 ||
      POSConfigModule.groupId === 19066 ||
      POSConfigModule.groupId === 19755 ||
      POSConfigModule.groupId === 19763 ||
      POSConfigModule.groupId === 19786 ||
      import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev";
    return b;
  });

  const confirmedAmountsInfo = computed(() => {
    const b = BookingModule.confirmedAmounts;
    return b;
  });

  const confirmedAmountsTotal = computed(() => {
    const b = BookingModule.confirmedAmountsTotal;
    return b;
  });

  const splitPayments = computed(() => {
    // console.log("start of splitPayments");
    // We show completed accountings as split payments. And we also show with them
    // a cash payment that has been added by the user but hasn't been confirmed yet
    // and we also show with them any pending Invoice (no need to be confirmed)
    // and we also show with them any payment code that is applied in this booking session
    const paidAmounts = BookingModule.selectedBooking?.accountings
      .filter(
        (acc) =>
          acc.transactionType === 1 &&
          // roundings get their own summary row rather than a payment row
          acc.paymentMethod !== PaymentMethods.METHOD_CASH_ROUNDING &&
          (acc.state === AccountingState.Completed ||
            (acc.state === AccountingState.Pending &&
              !acc.id /*making sure it is not coming from backend*/ &&
              (acc.promocode || acc.promoCodeUuid)) ||
            (acc.state === AccountingState.Pending &&
              (acc.paymentMethod === PaymentMethods.METHOD_POS_CASH ||
                acc.paymentMethod ===
                  PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer)))
      )
      .map((acc) => {
        return {
          translatedMethod:
            acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER
              ? String(
                  translatePaymentMethods(acc.paymentMethod) +
                    " " +
                    String(acc.promocode)
                )
              : translatePaymentMethods(acc.paymentMethod),
          method: acc.paymentMethod,
          accountingUuid: acc.uuid,
          accountingId: acc.id,
          state: acc.state,
          updatedAt: acc.updatedAt,
          createdAt: acc.createdAt,
          currency: currencies.value.find(
            (c) => c.label === acc.paymentCurrency
          ),
          amount: sumArray(
            acc.accountingEntriesList.map((it) => it.value / 100)
          ),
          exchangeRate: acc.exchangeRate,
          cashBill: acc.providedCashBill ? acc.providedCashBill / 100 : 0,
          isVoucherPaymentPending:
            acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
            acc.state === AccountingState.Pending
              ? true
              : false,
        };
      })
      .flat();
    console.log("paidAmounts", paidAmounts);
    return paidAmounts;
  });

  const currenciesUsedInPayment = computed(() => {
    const currencies: Currency[] = [];
    if (!splitPayments.value?.length) {
      return currencies;
    }
    splitPayments.value.forEach((splitPayment) => {
      if (
        !splitPayment?.currency ||
        currencies.includes(splitPayment.currency)
      ) {
        return;
      }
      currencies.push(splitPayment.currency);
    });
    return currencies;
  });

  const amountPaid = computed(() => {
    const b = BookingModule.amountPaid;

    return b;
  });

  const translatePaymentMethods = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "CASH":
        return t("settings.payment-methods.cash");
      case "POS_CARD":
        return t("payment-method.CARD");
      case "INVOICE":
        return t("payment-method.INVOICE");
      case "TO_BE_INVOICED":
        return t("payment-method.TO_BE_INVOICED");
      case "BANK_TRANSFER":
        return t("payment-method.BANK_TRANSFER");
      case "UNKNOWN":
        return t("payment-method.UNKNOWN");
      case "POS_ADYEN":
        return t("payment-method.POS_ADYEN");
      // case "DISCOUNT":
      //   return t("button.discount");
      case "POS_DISCOUNT":
        return t("payment-method.POS_DISCOUNT");
      case "FREE":
        return t("payment-method.FREE");
      case "CHEQUE":
        return t("payment-method.CHEQUE");
      case "CHEQUE_VACANCES":
        return t("payment-method.CHEQUE_VACANCES");
      case "CHEQUE_CULTURE":
        return t("payment-method.CHEQUE_CULTURE");
      case "PASS_CULTURE":
        return t("payment-method.PASS_CULTURE");
      case "CHORUS":
        return t("payment-method.CHORUS");
      // case "CE":
      //   return t("button.CE");
      // case "OCCYG":
      //   return t("button.OCCYG");
      case "POS_PROMO_CODE":
        return t("payment-method.POS_PROMO_CODE");
      case "POS_VOUCHER":
        return t("payment-method.VOUCHER");
      case "GIFT_CARD":
        return t("payment-method.GIFT_CARD");
      case "POS_MEMBERSHIP":
        return t("payment-method.POS_MEMBERSHIP");
      case "AMEX":
        return t("payment-method.AMEX");
      case "CE":
        return t("payment-method.CE");
      case "COUPON_SPORT":
        return t("payment-method.COUPON_SPORT");
      case "DISCOUNT":
        return t("payment-method.DISCOUNT");
      case "EXPERIENCEBANK":
        return t("payment-method.EXPERIENCEBANK");
      case "EXTERNAL_VOUCHER":
        return t("payment-method.EXTERNAL_VOUCHER");
      case "invitation":
        return t("payment-method.invitation");
      case "KIOSK_ADYEN":
        return t("payment-method.KIOSK_ADYEN");
      case "MASTERCARD":
        return t("payment-method.MASTERCARD");
      case "MEMBERSHIP":
        return t("payment-method.MEMBERSHIP");
      case "OCCYG":
        return t("payment-method.OCCYG");
      case "PAYPAL":
        return t("payment-method.PAYPAL");
      case "POS_ADYEN_CREATE_ORDER":
        return t("payment-method.POS_ADYEN_CREATE_ORDER");
      case "POS_BANK_TRANSFER":
        return t("payment-method.POS_BANK_TRANSFER");
      // case "POS_CARD":
      // return t("payment-method.POS_CARD");
      case "POS_CASH":
        return t("payment-method.POS_CASH");
      case "POS_EXTERNAL":
        return t("payment-method.POS_EXTERNAL");
      case "POS_GIFT_CARD":
        return t("payment-method.POS_GIFT_CARD");
      case "POS_INVITATION":
        return t("payment-method.POS_INVITATION");
      case "POS_INVOICE":
        return t("payment-method.POS_INVOICE");
      case "POS_TWINT":
        return t("payment-method.POS_TWINT");
      case "POSTCARD":
        return t("payment-method.POSTCARD");
      case "POSTEFINANCE":
        return t("payment-method.POSTEFINANCE");
      case "PROMO_CODE":
        return t("payment-method.PROMO_CODE");
      case "QOQA_VOUCHER":
        return t("payment-method.QOQA_VOUCHER");
      case "REFUNDED_VOUCHER":
        return t("payment-method.REFUNDED_VOUCHER");
      case "SPONSORED":
        return t("payment-method.SPONSORED");
      case "STAND_ALONE_ADYEN":
        return t("payment-method.STAND_ALONE_ADYEN");
      case "TWINT":
        return t("payment-method.TWINT");
      case "VISA":
        return t("payment-method.VISA");
      case "POS_ADYEN_PAY_BY_LINK":
        return t("payment-method.POS_ADYEN_PAY_BY_LINK");
      case "POS_REKA":
        return t("payment-method.POS_REKA");
      case "POS_DIVERSE_VOUCHER":
        return t("payment-method.POS_DIVERSE_VOUCHER");

      case "CFF":
        return t("payment-method.cff");
      case "CGN":
        return t("payment-method.cgn");
      case "QOQA":
        return t("payment-method.qoqa");
      case "DEINDEAL":
        return t("payment-method.deindeal");
      case "REALDEAL":
        return t("payment-method.realdeal");
      case "EXTERNAL_INCERT_VOUCHER":
        return t("payment-method.Incert_Voucher");

      case "Swiss_QR_Code_Bank_Transfer":
        return t("payment-method.BANK_TRANSFER_SWISS_QR");

      case "BANK_TRANSFER_SWISS_QR":
        return t("payment-method.BANK_TRANSFER_SWISS_QR");

      case "BUY_CLUB":
        return t("payment-method.BUY_CLUB");
      case "POS_BIMPLI_CADO":
        return t("payment-method.POS_BIMPLI_CADO");
      case "POS_KADEOS_INFINI":
        return t("payment-method.POS_KADEOS_INFINI");
      case "POS_ANCV_CONNECT":
        return t("payment-method.POS_ANCV_CONNECT");
      case "TOUT_UN_CANTON":
        return t("payment-method.TOUT_UN_CANTON");
      case "CHAPLINS":
        return t("payment-method.CHAPLINS");
      case "HELLO_CSE":
        return t("payment-method.HELLO_CSE");
      case "Z_CH_GUTSCHEIN":
        return t("payment-method.Z_CH_GUTSCHEIN");
    }
  };

  const payingCurrency = computed(() => {
    const b = BookingModule.selectedBooking?.currency;
    return b;
  });

  ///////////////////////////////////
  function showTicketInfo(pss: IQuantifiedProductSlotState): boolean {
    // Show ticket product info (startDateTime, priceName ...etc) only if the product is not a merch product
    const b =
      // bookingStep.value !== BookingStep.Payment &&
      pss.pssType === PSSType[PSSType.Ticket];
    return b;
  }

  const closeCalculator = () => {
    calcVisibility.value = !calcVisibility.value;
    BookingModule.setCalcValue("0");
  };

  const getCustomerFullName = computed<string>(() => {
    const customer = CustomersModule.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      return customer.fullName;
    }
    return t("asideordersummary.assign-customer");
  });

  const hasAppliedDiscount = computed<boolean>(() => {
    const b =
      !!BookingModule.selectedBookingDiscountTrack &&
      BookingModule.selectedBookingDiscountTrack.isApplied;
    return b;
  });

  const isDiscountOnAllOrder = computed<boolean>(() => {
    const b = BookingModule.selectedBookingDiscountTrack?.isOnAllOrder || false;
    return b;
  });

  const discountTotal1 = computed<number>(() => {
    const b = discountTotal.value;
    return b;
  });

  const whatIsLeftToPay = computed(() => {
    let b = "";
    b = (restToPay.value / 100).toFixed(2);
    return b;
  });

  const whatIsLeftToPayForInvoice = computed(() => {
    let b = "";
    b = (restToPayForInvoice.value / 100).toFixed(2);
    return b;
  });

  const total1 = computed(() => {
    // total of a booking after discount/promcodeDiscount
    const b = (bookingTotalConsideringDiscounts.value / 100).toFixed(2);
    return b;
  });

  // total tax amount
  const totalTax = computed(() => {
    return (bookingTotalTax.value / 100).toFixed(2);
  });

  const total = computed(() => {
    const b = bookingTotalConsideringDiscounts.value.toFixed(2);
    return b;
  });

  const balance1 = computed(() => {
    const b = restToPay.value / 100;
    return b;
  });

  const orderValueExcludingCancelledTickets = computed(() => {
    const b = (bookingTotalConsideringDiscounts.value / 100).toFixed(2);
    return b;
  });

  const isEditOrder = ref(false);

  watchEffect(async () => {
    if (BookingModule.selectedBookingUUID) {
      isEditOrder.value = await isBookingInDexie(
        BookingModule.selectedBookingUUID
      );
    }
  });

  const bookingSynchingDateTime = ref("");
  watchEffect(async () => {
    const syncedBooking = await getUntypedBookingByUuid(
      selectedBooking?.value?.uuid
    );

    bookingSynchingDateTime.value = syncedBooking?.creationDateTime || "";
  });

  const alreadyPaidAmount = computed(() => {
    const b = paymentAndDiscountTotal.value / 100;
    return b;
  });

  const refundedAmount = computed(() => {
    const refundAccountings = selectedBooking.value?.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.refund === 1 &&
        // roundings get their own summary row rather than a refund row
        acc.paymentMethod !== PaymentMethods.METHOD_CASH_ROUNDING &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND
    );
    let aEntries: number[] = [];
    if (refundAccountings && refundAccountings.length) {
      aEntries = refundAccountings
        .map((ac) => ac.accountingEntriesList)
        .flat()
        .map((ae) => ae.value);
    }
    const b = (-1 * sumArray(aEntries)) / 100;
    return b;
  });

  /**
   * Belgian cash rounding booked against the booking, in cents.
   */
  const roundingTotal = computed(() => BookingModule.roundingTotal);

  const isLongTitle = computed(() => {
    return String(t("asideordersummary.title")).length > 15;
  });

  const doesSupportSplitPayment = computed(() => {
    const b = BookingModule.doesSupportSplitPayment;
    return b;
  });

  const groupCurrencies = computed(() => {
    return POSConfigModule.groupCurrencies;
  });

  const currencies = computed(() => {
    const currencies = [];
    const startAmounts = CashFloatModule.cashFloat?.startAmounts;
    for (const currency in startAmounts) {
      const currencyInfo = groupCurrencies.value.find(
        (c) => c.label === currency
      );
      if (currencyInfo) {
        currencies.push(currencyInfo);
      }
    }
    return currencies;
  });

  const groupCurrency = computed(() => {
    return (
      currencies.value.find((c) => c.isDefault === 1) || currencies.value[0]
    );
  });

  const onClickCustomers = () => {
    assignOrEditCustomer();
  };

  /**
   * Takes a list of PSS related to one category, and use any pss Id of them to get the discounted price value from discount tracking object
   */
  function getPSSNewPriceValue(pssGroup: IQuantifiedProductSlotState) {
    const discountTracking = BookingModule.selectedBookingDiscountTrack;
    const priceValue = discountTracking?.pssDiscounts.find(
      (p) => p.pssUUID === String(pssGroup.pssIds[0])
    )?.pssNewPriceValue;

    return priceValue || 0;
  }

  /**
   * Generic Discount
   * Value returned is fully based on accountings
   * e.g: Discount 50% / CHF 6.00
   * The discount can be coming from a promocodeDiscount or a normal discount or both
   * Or from DISCOUNT accounting
   */
  function getTicketDiscount1(pssGroup: IQuantifiedProductSlotState) {
    // all pss that are concerned in discount/promocodeDiscount
    let discountAccEntries: IAccountingEntry[] = [];
    const discountAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          !acc.refund &&
          !acc.promoCodeId &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          acc.paymentMethod !== PaymentMethods.METHOD_OFFER &&
          acc.paymentMethod !== PaymentMethods.METHOD_POS_OFFER &&
          (acc.state === AccountingState.Completed ||
            acc.state === AccountingState.Pending)
      );
    if (!discountAccountings || !discountAccountings.length) {
      // there is no discount accountings
      return "";
    }

    discountAccEntries = discountAccountings
      .map((acc) => acc.accountingEntriesList)
      .flat();

    // a list of the uuids of the pssGroup
    const rowPssListUuids = pssGroup.pssIds;
    // list of pss from the pssGroup and that are considered in the discount/promocodeDiscount
    const filteredPssDiscounts = discountAccEntries.filter((ae) =>
      rowPssListUuids.some(
        (element) => element === ae.productSlotStateUuid && ae.value > 0
      )
    );
    if (filteredPssDiscounts && filteredPssDiscounts.length) {
      const pssGroupPricesSum = pssGroup.quantity * getPSSValue(pssGroup);
      const pssGroupDiscountsSum = sumArray(
        filteredPssDiscounts.map((it) => it.value)
      );

      const newPriceSum = pssGroupPricesSum - pssGroupDiscountsSum;

      return (
        t("discount.discount") +
        " " +
        (((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) * 100).toFixed(
          0
        ) +
        "%" +
        " / " +
        ((pssGroupPricesSum - newPriceSum) / 100).toFixed(2) +
        " " +
        String(selectedBooking.value?.currency)
      );
    }
    return "";
  }

  /**
   * Promocode Discount
   * Value returned is fully based on accountings
   * e.g: ABDCEF 50% / CHF 6.00 -applied
   * The discount can be coming from a promocodeDiscount or a normal discount or both
   * Or from DISCOUNT accounting
   */
  function getTicketAppliedDiscountCodes(
    pssGroup: IQuantifiedProductSlotState
  ): string[] {
    const promocodeAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.promoCodeId &&
          acc.refund !== 1 &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          acc.paymentMethod !== PaymentMethods.METHOD_OFFER &&
          acc.paymentMethod !== PaymentMethods.METHOD_POS_OFFER &&
          (acc.state === AccountingState.Completed ||
            acc.state === AccountingState.Pending)
      );

    if (!promocodeAccountings || !promocodeAccountings.length) {
      // there are no promocode discount accountings
      return [];
    }

    const rowPssListUuids = pssGroup.pssIds;
    const pssGroupPricesSum = pssGroup.quantity * getPSSValue(pssGroup);
    const lines: string[] = [];
    for (const acc of promocodeAccountings) {
      const promocodeAEs = acc.accountingEntriesList.filter((ae) =>
        rowPssListUuids.some(
          (element) => element === ae.productSlotStateUuid && ae.value > 0
        )
      );
      if (!promocodeAEs || !promocodeAEs.length) continue;
      const pssGroupDiscountsSum = sumArray(promocodeAEs.map((it) => it.value));
      const newPriceSum = pssGroupPricesSum - pssGroupDiscountsSum;
      const line = `${String(acc.promocode)} ${(
        ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
        100
      ).toFixed(0)}% / ${((pssGroupPricesSum - newPriceSum) / 100).toFixed(
        2
      )} ${String(selectedBooking.value?.currency)}`;

      lines.push(line);
    }
    return lines;
  }

  /**
   * Finds discounts and promocodes applied on a ticket that are part of offers
   * Value returned is fully based on accountings
   * e.g: Offer ("Promocode" or "Discount") 50% / CHF 6.00 -applied
   */
  function getTicketAppliedOffers(
    pssGroup: IQuantifiedProductSlotState
  ): string[] {
    const offerAccountings = BookingModule.selectedBooking?.accountings.filter(
      (acc) =>
        acc.refund !== 1 &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
        (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
        (acc.state === AccountingState.Completed ||
          acc.state === AccountingState.Pending)
    );

    const offerRefundAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.refund === 1 &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
            acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
          acc.state === AccountingState.Completed
      );

    if (
      !offerAccountings ||
      !offerAccountings.length ||
      !offerRefundAccountings
    ) {
      // there are no offer accountings
      return [];
    }

    const rowPssListUuids = pssGroup.pssIds;
    const pssGroupPricesSum = pssGroup.quantity * getPSSValue(pssGroup);
    const lines: string[] = [];
    const refundAccountings: (string | null)[] = [];
    for (const acc of offerAccountings) {
      const offerAes = acc.accountingEntriesList.filter((ae) =>
        rowPssListUuids.some(
          (element) => element === ae.productSlotStateUuid && ae.value > 0
        )
      );
      if (!offerAes || !offerAes.length) continue;
      // check if accounting has been refunded, in which case don't include it in the summary if the pss group is confirmed
      const refundAcc =
        pssGroup.bookingStatus === BookingStatus.Confirmed
          ? isOfferAccountingRefunded(
              acc,
              offerRefundAccountings,
              refundAccountings
            )
          : null;
      if (refundAcc) {
        refundAccountings.push(refundAcc.uuid);
        continue;
      }
      const pssGroupDiscountsSum = sumArray(offerAes.map((it) => it.value));
      const newPriceSum = pssGroupPricesSum - pssGroupDiscountsSum;
      let line = "";
      if (acc.promocode) {
        const offerName =
          (OffersModule.offersCommon as any)?.offers?.find(
            (offer: any) => offer.id === offerAes[0].offerId
          )?.name || t("label.offer-discount");
        line = `${offerName} ${String(acc.promocode)} ${(
          ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
          100
        ).toFixed(0)}% / ${((pssGroupPricesSum - newPriceSum) / 100).toFixed(
          2
        )} ${String(selectedBooking.value?.currency)}`;
      } else {
        // use the offer name or a generic "Discount Offer" if not found
        const offerName =
          (OffersModule.offersCommon as any)?.offers?.find(
            (offer: any) => offer.id === offerAes[0].offerId
          )?.name || t("label.offer-discount");
        line = `${offerName} ${(
          ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
          100
        ).toFixed(0)}% / ${((pssGroupPricesSum - newPriceSum) / 100).toFixed(
          2
        )} ${String(selectedBooking.value?.currency)}`;
      }

      lines.push(line);
    }
    return lines;
  }

  /**
   *
   * Whether or not a given pss is considerd in the promocodeDiscount or the discount
   * we identify it using discount accountings
   */
  function isPSSConsideredInDiscount1(pssGroup: IQuantifiedProductSlotState) {
    // const pssValuesTracking = BookingModule.bookingLifeTimePssValues;
    let discountAccEntries: IAccountingEntry[] = [];
    const discountAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          !acc.refund &&
          (acc.state === AccountingState.Completed ||
            (acc.state === AccountingState.Pending && !acc.id))
      );
    if (discountAccountings && discountAccountings.length) {
      discountAccEntries = discountAccountings
        ?.map((acc) => acc.accountingEntriesList)
        .flat();
    }

    const index = discountAccEntries.findIndex(
      (ae) => ae.productSlotStateUuid === pssGroup.uuid
    );

    if (index === -1) {
      // there is no discount for this pss
      return false;
    } else {
      return true;
    }
  }

  /**
   * Whether or not these pss belongs to a generic Discount accounting
   */
  function hasGenericDiscountAccountings(
    pssGroup: IQuantifiedProductSlotState
  ): boolean {
    // a list of the uuids of the pssGroup
    const rowPssListUuids = pssGroup.pssIds;
    const discountAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.state === AccountingState.Completed &&
          (acc.paymentMethod === PaymentMethods.METHOD_DISCOUNT ||
            acc.paymentMethod === PaymentMethods.METHODS_POS_DISCOUNT)
      );

    if (discountAccountings && discountAccountings.length) {
      const accEntries = discountAccountings
        .map((acc) => acc.accountingEntriesList)
        .flat();
      const filteredPssDiscounts = accEntries.filter((item) =>
        rowPssListUuids.some((element) => element === item.productSlotStateUuid)
      );
      if (filteredPssDiscounts && filteredPssDiscounts.length) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  /**
   *
   * Whether or not a given pss is considerd in the discount
   */
  function isPSSConsideredInDiscount(pssGroup: IQuantifiedProductSlotState) {
    const discountPssList =
      BookingModule.selectedBookingDiscountTrack?.pssDiscounts;
    if (!discountPssList) {
      // there is no discount
      return false;
    }
    if (
      discountPssList.findIndex((pss) => pss.priceId === pssGroup.priceId) > -1
    ) {
      return true;
    }
    return false;
  }

  /**
   *
   * Whether or not a given pssGroup is a free ticket or not
   */
  function isFreeTicket(pssGroup: IQuantifiedProductSlotState) {
    const value = getPSSValue(pssGroup);
    const _discountValue = pssGroup._discountValue;
    return value === 0 && _discountValue === 0;
  }

  /**
   * Removes an accounting from the booking
   * It reset paymentStatus of the pss linked to the removed payment
   */
  function removePayment(accountingUuid: string) {
    const booking = selectedBooking.value;
    if (!booking) return;
    const updatedBooking = Object.assign({}, booking);
    const accountings = updatedBooking.accountings;
    const affectedPss = accountings
      .find((acc) => acc.uuid === accountingUuid)
      ?.accountingEntriesList.map((ae) => ae.productSlotStateUuid);
    // remove the accounting
    let accountingToRemove = null;
    const index = accountings.findIndex((a) => a.uuid === accountingUuid);
    if (index > -1) {
      accountingToRemove = cloneDeep(accountings[index]);
      // If user removed a pending invoice accounting then we should flag it as abondoned only,
      // if user removed any other type of payment then we remove it the old way by removing the object from accountings array
      if (
        accountingToRemove.paymentMethod ===
        PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer
      ) {
        // just flag the accounting as cancelled
        accountings[index].state = AccountingState.Cancelled;
      } else {
        // remove the accounting from the array
        accountings.splice(index, 1);
      }
    }
    // reset pss paymentStatus
    const pssList = updatedBooking.pssList;
    for (let i = 0; i < pssList.length; i++) {
      if (affectedPss?.includes(pssList[i].uuid)) {
        // remove TICKET accounting of the pss if it has no payment
        const pssPaymentAccEntries = updatedBooking.accountings
          .filter(
            (acc) =>
              acc.transactionType ===
                TransactionTypes.TRANSACTION_TYPE_PAYMENT && acc.state === 5
          )
          .map((acc) => acc.accountingEntriesList)
          .flat()
          .filter((ae) => ae.productSlotStateUuid === pssList[i].uuid)
          .map((ae) => ae.value);
        const pssPayment = sumArray(pssPaymentAccEntries);
        if (!pssPayment) {
          pssList[i].paymentStatus = PaymentStatus.NotStarted;
        }
      }
    }
    BookingModule.updateBooking(updatedBooking);

    // Now, if the payment we have just removed were generated by a code (voucher/giftcard) then we need to remove the applied code
    // from BookingModule if there is no other payment generated by the same code
    if (accountingToRemove) {
      if (
        accountingToRemove.transactionType ===
        TransactionTypes.TRANSACTION_TYPE_PAYMENT
      ) {
        if (
          accountingToRemove.paymentMethod === PaymentMethods.POS_GIFT_CARD ||
          accountingToRemove.paymentMethod === PaymentMethods.POS_VOUCHER
        ) {
          // So the removed accounting was generated by a payment code, lets remove the promocode from BookingModule.promocodes
          // but we remove only if there is no other payments related to this code (may be an accounting coming from backend)
          const codeId = accountingToRemove.promoCodeId;
          const codeUuid =
            accountingToRemove.promoCodeUuid || accountingToRemove.promocode;
          const completedAccountingsFromBackend =
            updatedBooking.accountings.filter(
              (acc) =>
                acc.state === AccountingState.Completed &&
                (acc.promoCodeId === codeId ||
                  acc.promocode === codeUuid ||
                  acc.promoCodeUuid === codeUuid) &&
                !acc.id
            );
          if (
            completedAccountingsFromBackend &&
            completedAccountingsFromBackend.length
          ) {
            // we don't remove the promocode from the booking module
          } else {
            const bookingUuid = accountingToRemove.bookingUuid;
            BookingModule.removePromocode({
              bookingUuid: String(bookingUuid),
              promocodeUuid: String(accountingToRemove.promoCodeUuid),
            });
          }
        }
        // If it is an incert accounting then we need to update the tracking of the consumption of the voucher in the current order session
        if (
          accountingToRemove.paymentMethod ===
          PaymentMethods.EXTERNAL_INCERT_VOUCHER
        ) {
          const voucherCodeInAccounting = accountingToRemove.promocode;
          const voucherInfo = ExternalVouchersModule.externalVouchers.find(
            (v) =>
              v.voucherCode === voucherCodeInAccounting &&
              v.orderSessionId === String(BookingModule.currentOrderSessionId)
          );
          if (voucherInfo) {
            const totalValue = sumArray(
              accountingToRemove.accountingEntriesList.map((ae) => ae.value)
            );
            const voucherInfoCopy = cloneDeep(voucherInfo);
            voucherInfoCopy.currentSessionVoucherConsumption =
              voucherInfo.currentSessionVoucherConsumption - totalValue;
            ExternalVouchersModule.updateExternalVoucher(voucherInfoCopy);
          }
        }
      }
    }
  }

  /**
   * Deletes a confirmed amount in case of Cash, and restore pss.priceValue/pss.paymentStatus
   * In case of Cards, it does nothing, because Cards confirmed amounts are created when we Complete Order and we wait for Adyen response
   * If the accounting is completed avoid removing it
   */
  function deleteConfirmedAmount(id: string) {
    if (!BookingModule.selectedBooking) return;
    const boookingUUID = BookingModule.selectedBooking.uuid;
    // 1- delete corresponding confirmedAmountTracking object
    const accountingInfo = BookingModule.bookingsAmountsTrack[
      String(boookingUUID)
    ].confirmedAmounts.find((c) => c.id === id)?.accountingInfo;
    if (!accountingInfo) {
      return;
    }
    if (accountingInfo.paymentToolId !== PaymentMethodTypes.Cash) {
      // This is not a cash confirmed amount, lets ignore it
      return;
    }
    const accountingUUId = accountingInfo.accountingUUId;
    // If the accounting is completed we don't remove it (we should never come to this point because remove button is hidden)
    const accounting = BookingModule.selectedBooking.accountings.find(
      (ac) => ac.uuid === accountingUUId
    );
    if (accounting && accounting.state === AccountingState.Completed) {
      // This accounting is completed, do nothing
      return;
    }

    BookingModule.deleteConfirmedAmount(id);

    // 2- delete the linked accounting object from the booking
    // and reset pss.paymentStatus to 1 (NotStarted) and if there is discount applied we restore originalPriceValue to pss.priceValue
    // (creating a new booking object to avoid updating the original object in place)
    const booking = BookingModule.selectedBooking;
    const updatedBooking = Object.assign({}, booking);
    const accountings = updatedBooking.accountings;
    const index = accountings.findIndex((a) => a.uuid === accountingUUId);
    if (index > -1) {
      accountings.splice(index, 1);
    }
    const pssList = updatedBooking.pssList;
    for (const pss of pssList) {
      // restore paymentStatus to notStarted
      pss.paymentStatus = PaymentStatus.NotStarted;
    }
    BookingModule.updateBooking(updatedBooking);
  }

  /**
   * returns whether or not a given accounting is completed
   */
  const isAccountingCompleted = computed((/*accountingUuid: string*/) => {
    // We return a function that will do the computation
    return (accountingUuid: string) => {
      if (!BookingModule.selectedBooking) return false;
      const accountingUUId = accountingUuid;
      const accounting = BookingModule.selectedBooking.accountings.find(
        (ac) => ac.uuid === accountingUUId
      );
      if (accounting && accounting.state === AccountingState.Completed) {
        return true;
      }
      return false;
    };
  });

  const orderReference = computed(() => {
    return selectedBooking.value?.reference;
  });

  /**
   * Decides whether to delete confirmed tickets or unconfirmed tickets
   * pssIds are of tickets of having the same bookingStatus
   * so if one of the pss is confirmed then all the pss having these pssIds are confirmed
   */
  async function deleteTickets(pssIds: string[]) {
    // Get the booking status of one of these pss
    const pssList = BookingModule.selectedBooking?.pssList;
    if (!pssList || !pssList.length) {
      return;
    }
    // find and keep minimum quantity if the deleted pss is a mandatory addon
    let addonMinQuantity = 0;
    for (let i = 0; i < pssIds.length; i++) {
      const pssId = pssIds[i];
      const pssToDelete = pssList.find((pss) => pss.uuid === pssId);
      if (pssToDelete?.mainTicketUUID) {
        const mainPCPSS = pssList.find(
          (pss) => pss.uuid === pssToDelete.mainTicketUUID
        );
        if (mainPCPSS) {
          const mainPC = await getProductCategoryByPriceId(mainPCPSS?.priceId);
          const mainPCPrice = mainPC?.prices.find(
            (price) => price.id === mainPCPSS.priceId
          );
          if (mainPCPrice) {
            const addon = mainPCPrice.addons.find(
              (addon) => addon.priceId === pssToDelete.priceId
            );
            if (
              addon?.isMandatory &&
              addon?.minQuantity &&
              addon.minQuantity > 0
            ) {
              addonMinQuantity = addon.minQuantity;
              break;
            }
          }
        }
      }
    }
    if (addonMinQuantity) {
      NotificationService.showCustomMessageWithTitle(
        `A minimum quantity of ${addonMinQuantity} is required for this addon`,
        "Warning",
        "warning"
      );
      if (pssIds.length <= addonMinQuantity) {
        return;
      } else {
        pssIds = pssIds.slice(0, pssIds.length - addonMinQuantity);
      }
    }

    const pssIdsString: string = pssIds.join(" - ");
    const pssListToDelete = pssList.filter((pss) =>
      pssIdsString.includes(String(pss.uuid))
    );
    // if they are confirmed then we show MessageBox
    if (!pssListToDelete || !pssListToDelete.length) {
      return;
    }
    const bookingStatus: BookingStatus = pssListToDelete[0].bookingStatus;
    if (bookingStatus === BookingStatus.Confirmed) {
      if (pssListToDelete.length === 1) {
        // For a single ticket, skip the selection modal and show direct confirmation
        openMessageBox(pssListToDelete[0]);
        return;
      }

      // Show only the selected confirmed group
      confirmedPSSToDelete.value = pssListToDelete;

      LogService.info(
        `Showing tickets cancellation modal for pssList: ${confirmedPSSToDelete.value
          .map((p) => p.uuid)
          .join(" - ")}`
      );
      // showing confirmed tickets popup
      showConfirmedTicketsRemovalPopup();
    } else {
      // pss to be deleted are not confirmed then we just delete them the old way
      // lets also get all the related addonPSS to be deleted as well
      const addonsPSSIds: string[] = pssList
        .filter(
          (pss) =>
            pss.mainTicketUUID && pssIdsString.includes(pss.mainTicketUUID)
        )
        .map((pss) => String(pss.uuid));
      pssIds.push(...addonsPSSIds);
      const releaseSeats = true; // We need to release any seats related to their pss
      await deleteUnconfirmedTickets(pssIds, releaseSeats);
      // Delete custom fields from booking.customFieldsAnswers if none of their pss still exists
      LogService.info(
        `Deleting custom fields from booking.customFieldsAnswers if none of their pss still exists, for booking: ${BookingModule.selectedBooking?.uuid}`
      );
      clearBookingCustomFields();
    }
  }

  /**
   * Shows popup for confirmed tickets removal
   */
  function showConfirmedTicketsRemovalPopup() {
    // Show a popup to show a list of confirmed pss with cancel button for each pss in a modal (basic design first)
    showCancelTicketsModal.value = true;
    selectedConfirmedPssUuids.value = [];
  }

  function closeModal() {
    showCancelTicketsModal.value = false;
    confirmedPSSToDelete.value = [];
    selectedConfirmedPssUuids.value = [];
  }

  function closeCancelConfirmationModal() {
    showCancelConfirmationModal.value = false;
    cancelConfirmationPss.value = [];
  }

  function openCancelConfirmationModal(pssList: IProductSlotState[]) {
    cancelConfirmationPss.value = pssList;
    showCancelConfirmationModal.value = true;
  }

  async function confirmTicketsCancellation() {
    if (!cancelConfirmationPss.value.length || isRemoveButtonLoading.value) {
      return;
    }

    isRemoveButtonLoading.value = true;
    try {
      if (cancelConfirmationPss.value.length === 1) {
        await proceedConfirmedTicketRemoval(
          String(cancelConfirmationPss.value[0].uuid)
        );
      } else {
        await proceedConfirmedTicketRemovalBatch(
          cancelConfirmationPss.value.map((pss) => String(pss.uuid))
        );
      }
      closeCancelConfirmationModal();
    } finally {
      isRemoveButtonLoading.value = false;
    }
  }

  function isConfirmedPssSelected(pssUuid: string): boolean {
    return selectedConfirmedPssUuids.value.includes(pssUuid);
  }

  function toggleConfirmedPssSelection(pssUuid: string) {
    if (isRemoveButtonLoading.value) return;

    if (selectedConfirmedPssUuids.value.includes(pssUuid)) {
      selectedConfirmedPssUuids.value = selectedConfirmedPssUuids.value.filter(
        (uuid) => uuid !== pssUuid
      );
      return;
    }

    selectedConfirmedPssUuids.value = [
      ...selectedConfirmedPssUuids.value,
      pssUuid,
    ];
  }

  function toggleSelectAllConfirmedPss(value: boolean) {
    if (isRemoveButtonLoading.value) return;

    if (value) {
      selectedConfirmedPssUuids.value = confirmedPSSToDelete.value.map((pss) =>
        String(pss.uuid)
      );
      return;
    }

    selectedConfirmedPssUuids.value = [];
  }

  async function bulkCancelSelectedTickets() {
    if (
      !selectedConfirmedPssUuids.value.length ||
      isRemoveButtonLoading.value
    ) {
      return;
    }

    const selectedPss = confirmedPSSToDelete.value.filter((pss) =>
      selectedConfirmedPssUuids.value.includes(String(pss.uuid))
    );
    if (!selectedPss.length) return;

    if (
      !isOnline.value &&
      selectedPss.some(
        (pss) =>
          pss.type === PCTypes.SeatedSub ||
          pss.type === PCTypes.MembershipSeated
      )
    ) {
      openInternetNeededMessageBox();
      return;
    }

    openCancelConfirmationModal(selectedPss);
  }

  /**
   * change bookingStatus of the pss to Cancelled and we create TICKET accounting for the corresponding ticket
   * It also considers taxes in the cancellation:
   * i.e: creates a tax acc with refund:1 if we don't have one yet, AND it create TAX ae for cancelled pss with positive value
   */
  async function proceedConfirmedTicketRemovalBatch(pssUuids: string[]) {
    if (!pssUuids.length) return;
    // if user confirm message box we change bookingStatus of the pss to Cancelled and we create TICKET accounting for the corresponding ticket
    // And if this pss was discounted using generic discount then we need to create the POS_Discount refund accounting for it
    // 1- cancel pss.bookingStatus
    const booking = BookingModule.selectedBooking;
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }
    const bookingCopy = cloneDeep(booking);

    /**
     * discount accountings are non-refund accountings that are either:
     * 1- promocode accountings (promocodeId exists)
     * 2- generic discount accountings (payment method POS_DISCOUNT)
     * 3- discount accountings generated by offers (payment method POS_OFFER and OFFER)
     */
    // L3-7623: also require state === Completed so voided (state=7) accountings
    // left behind by removeAndReapplyPromocodes don't get refunded as ghosts.
    const discountAccountings: IAccounting[] = bookingCopy.accountings.filter(
      (acc) =>
        (acc.promoCodeId ||
          acc.paymentMethod === PaymentMethods.METHODS_POS_DISCOUNT ||
          acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_OFFER) &&
        acc.refund !== 1 &&
        acc.state === AccountingState.Completed
    );

    const processedPssUuids = new Set<string>();

    // Update bookingStatus
    // then create TICKET acc for the cancellation operation
    // then create accountingEntry for tax
    // then create PROMOCODE acc for the cancellation if there's a promocode applied
    for (const pssUuid of pssUuids) {
      const cancelledPss = bookingCopy.pssList.find(
        (pss) => pss.uuid === pssUuid
      );
      if (!cancelledPss) {
        continue;
      }

      // lets also get all the related addonPSS that were not canceled already and cancel them as well
      const addons = bookingCopy.pssList.filter(
        (addon: IProductSlotState) =>
          addon.mainTicketUUID &&
          addon.mainTicketUUID === pssUuid &&
          addon.bookingStatus !== BookingStatus.Canceled &&
          addon.bookingStatus !== BookingStatus.Abandoned
      );

      const pssListToBeCancelled: IProductSlotState[] = [
        cancelledPss,
        ...addons,
      ];

      for (const pss of pssListToBeCancelled) {
        if (processedPssUuids.has(String(pss.uuid))) {
          continue;
        }
        processedPssUuids.add(String(pss.uuid));

        pss.bookingStatus = BookingStatus.Canceled;

        // 2- create TICKET accounting
        const ticketTypeAccounting = createCancelPSSAccounting(
          pss,
          bookingCopy.currency
        );
        bookingCopy.accountings.push(ticketTypeAccounting);

        // create AccEnts for of the cancelled pss
        LogService.info(
          `Going to create cancellation AE for attached pss ${pss.uuid}`,
          {
            params: pss,
          }
        );

        //////////////////////// EXTERNAL VOUCHER HERE
        // 1- Check if the cancelled ticket has external voucher payment
        // 2- Check in state if this voucher allows refund --> For now always true until statusCheck response gives more info
        // 3- Create the refund accounting
        const allowExternalVoucherRefund = true;
        if (allowExternalVoucherRefund) {
          const externalVouchersAccountings: IAccounting[] =
            bookingCopy.accountings.filter(
              (acc) =>
                acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
                acc.refund !== 1 &&
                acc.state === AccountingState.Completed
            );
          const externalVoucherAccs = externalVouchersAccountings.filter(
            (acc) =>
              acc.accountingEntriesList.some(
                (ae) => ae.productSlotStateUuid === pss.uuid
              )
          );
          if (externalVoucherAccs && externalVoucherAccs.length) {
            // create an external voucher accounting for the cancellation
            for (let i = 0; i < externalVoucherAccs.length; i++) {
              const externalVoucherRefundAccounting =
                createCancelPSSRefundIncertAccounting(
                  bookingCopy,
                  pss,
                  externalVoucherAccs[i]
                );
              if (externalVoucherRefundAccounting) {
                bookingCopy.accountings.push(externalVoucherRefundAccounting);

                // Lets now increase remainingValue of this voucher in the state just in case the user book more tickets
                const voucherInfo =
                  ExternalVouchersModule.externalVouchers.find(
                    (v) =>
                      v.voucherCode ===
                        externalVoucherRefundAccounting.promocode &&
                      v.orderSessionId ===
                        externalVoucherRefundAccounting.orderSessionId
                  );

                const voucherInfoCopy = cloneDeep(
                  voucherInfo
                ) as IExternalVoucher;
                const refundAccountingSum = sum(
                  externalVoucherRefundAccounting.accountingEntriesList.map(
                    (acE) => acE.value
                  )
                );
                voucherInfoCopy.originalRemainingValue =
                  Number(voucherInfo?.originalRemainingValue) +
                  -1 * refundAccountingSum;
                ExternalVouchersModule.updateRemainingValue(voucherInfoCopy);
              }
            }
          }
        }

        // check if there's a promocode/discount applied to this pss and cancel them
        let pssDiscountAccountings = discountAccountings.filter((acc) =>
          acc.accountingEntriesList.some(
            (ae) => ae.productSlotStateUuid === pss.uuid
          )
        );

        console.log(
          "----------------- going to call createCancelPSSDiscountAccounting 2"
        );
        ///////////////////////// SPECIAL TO OFFERS DISCOUNT
        // a cancelled ticket that has an offer discount applied to it should have a discount refund accounting created just like other type of discounts (promocodes & generic discounts) except in the below one case:
        // In Offers logic we can have a case where an offer refund discount accounting is already created for a not-cancelled ticket if the offer got not eligible anymore (in other sessions and/or in other apps)
        // It means NOT ALWAYS a cancelled ticket that has an offer discount applied to should have a discount refund accounting created for it
        // So lets make sure we're not in the case where this cancelled ticket has an offer discount and has already the offer refund accountings so that we do not recreate it
        const pssOfferRefundAccountings_originalAccountingUuids =
          bookingCopy.accountings
            .filter(
              (acc) =>
                acc.refund &&
                acc.paymentMethod === "POS_OFFER" &&
                acc.state === AccountingState.Completed &&
                acc.accountingEntriesList.some(
                  (accEntry) => accEntry.productSlotStateUuid === pss.uuid
                )
            )
            .map((acc) => acc.originalAccountingUuid);
        const pssDiscountAccountingsFiltered: IAccounting[] = [];
        pssDiscountAccountings.forEach((pssDiscountAccounting) => {
          if (
            !pssOfferRefundAccountings_originalAccountingUuids.includes(
              pssDiscountAccounting.uuid
            )
          ) {
            pssDiscountAccountingsFiltered.push(pssDiscountAccounting);
          }
        });
        pssDiscountAccountings = pssDiscountAccountingsFiltered;
        ///////////////////////// SPECIAL TO OFFERS DISCOUNT

        pssDiscountAccountings.forEach((pssDiscountAccounting) => {
          const discountRefundAccounting = createCancelPSSDiscountAccounting(
            bookingCopy,
            [pss],
            pssDiscountAccounting
          );
          if (discountRefundAccounting) {
            bookingCopy.accountings.push(discountRefundAccounting);
          }
        });

        // POS IS NOT SUPPOSED TO CREATE FEE ACCOUNTINGS EVEN FOR REFUND/CANCELLATION
        // check if the pss has a FEE then we create a refund accounting for it
        // if (pss.paymentFeeCollected > 0) {
        //   const feeAccounting = createCancelPSSFeeAccounting(
        //     pss,
        //     bookingCopy.currency
        //   );
        //   bookingCopy.accountings.push(feeAccounting);
        // }
      }
    }

    // // we need to keep all other accountings except the one that we want to update
    // // so we keep all accountings that are not type TAX(6) and the TAX accounting that is not for cancellation refund != 1
    // const otherAccountings = bookingCopy.accountings.filter(
    //   (acc) =>
    //     !(
    //       acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX &&
    //       acc.refund === 1
    //     )
    // );
    // here 'otherAccountings' will contains all the accountigs except the one that we've created/edited,
    // we need to add it
    // otherAccountings.push(taxAccounting);
    // now 'otherAccountings' has all the booking accoutings we just need to update the booking
    // bookingCopy.accountings = otherAccountings;

    BookingModule.updateBooking(bookingCopy);

    // Update the pss to show to be removed
    const pssUuidSet = new Set(pssUuids.map((pssUuid) => String(pssUuid)));
    confirmedPSSToDelete.value = confirmedPSSToDelete.value.filter(
      (ps) => !pssUuidSet.has(String(ps.uuid))
    );
    selectedConfirmedPssUuids.value = selectedConfirmedPssUuids.value.filter(
      (uuid) => !pssUuidSet.has(String(uuid))
    );
    if (!confirmedPSSToDelete.value || !confirmedPSSToDelete.value.length) {
      // No more pss to remove, lets close the modal
      closeModal();
    }

    // re-apply offers after pss removal
    await applyOffersToBooking(cloneDeep(bookingCopy));
  }

  async function proceedConfirmedTicketRemoval(pssUuid: string) {
    await proceedConfirmedTicketRemovalBatch([pssUuid]);
  }

  const openMessageBox = (pss: IProductSlotState) => {
    // If the user is not connected and he wants to cancel a seating PSS then we stop him
    // because we don't want him to be able to cancel the pss and leave the seat booked in seats io
    if (
      !isOnline.value &&
      (pss.type === PCTypes.SeatedSub || pss.type === PCTypes.MembershipSeated)
    ) {
      openInternetNeededMessageBox();
    } else {
      openCancelConfirmationModal([pss]);
    }
  };

  function isInThePast(pss: IQuantifiedProductSlotState): boolean {
    const enddateTime = dayjs(pss.endDateTime);
    const now = dayjs(new Date());
    const isBefore = enddateTime.isBefore(now);
    return isBefore;
  }
  /**
   * We show remove button in below cases only if the ticket is not an add-on:
   * Ticket not Cancelled yet
   * Ticket Confirmed but it is not in the past (except giftCards/MEmebership/MembershipVoucher)
   * In Order Step
   * Ticket not in the past or is a Retail
   */
  function showRemoveBtn(pss: IQuantifiedProductSlotState): boolean {
    if (pss.pcType === PCTypes.Donation) {
      // we don't let user cancel a donation
      return false;
    }
    if (BookingModule.bookingStep !== BookingStep.Order) {
      // remove button is displayed only in Order step
      return false;
    }
    if (pss.isAutomaticAddon) return false;
    if (
      pss.bookingStatus === BookingStatus.Canceled ||
      pss.bookingStatus === BookingStatus.Abandoned
    ) {
      // a cancelled/abandoned pss shouldn't have remove button shown
      return false;
    }
    if (pss.pssType === PSSType[PSSType.Retail]) {
      // Always show remove button for Retail
      return true;
    }
    if (
      pss.pcType === PCTypes.GiftCard ||
      pss.pcType === PCTypes.Membership ||
      pss.pcType === PCTypes.MembershipVoucher
    ) {
      // Always show remove button for giftCards/Memberships/MembershipVoucher
      return true;
    }
    if (isInThePast(pss) && pss.bookingStatus === BookingStatus.Confirmed) {
      // At this point we're left with normal tickets
      // don't show their remove button if their startDate is in the past
      // For VHS & MOI Palermo we allow cancelling past tickets
      if (
        POSConfigModule.isVHS ||
        POSConfigModule.groupId === 19682 ||
        POSConfigModule.isEdeis
      ) {
        return true;
      }

      // If FlipLab then we check whether these PSS was paid during the current sales session or not
      // if these pss were paid during the current sales session then we allow cancelling them
      // even if they are in the past. Else, we don't allow
      // (An exact way to define whether PSS was paid in a closed session is to take acc.cfUid
      // and do graphql call to check if cashfloat was closed or not; this is not possible though to do an api call before showing an icon)
      if (POSConfigModule.isFlipLab) {
        // lets check if these pss were paid during the current sales session
        const paymentAccountings =
          BookingModule.selectedBooking?.accountings.filter(
            (acc) =>
              !acc.promoCodeUuid &&
              acc.state === AccountingState.Completed &&
              acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT
          );
        const pssPaymentAccountings: IAccounting[] = [];
        paymentAccountings?.forEach((acc) => {
          // const pssUuids = acc.accountingEntriesList.map((ae) => ae.productSlotStateUuid);
          acc.accountingEntriesList.forEach((ae) => {
            if (pss.pssIds.includes(String(ae.productSlotStateUuid))) {
              pssPaymentAccountings.push(acc);
            }
          });
        });
        // If one of the pssPaymentAccountings is actually paid in the past and not today
        // then we don't allow cancelling
        for (let i = 0; i < pssPaymentAccountings.length; i++) {
          const currentDate = dayjs();
          const accCreationDate = dayjs(pssPaymentAccountings[i].createdAt);
          const isInPastAndNotToday =
            accCreationDate.isBefore(currentDate, "day") &&
            !accCreationDate.isSame(currentDate, "day");
          if (isInPastAndNotToday) {
            // accounting creation date is yesterday and further in the past; we don't allow cancelling the ticket
            return false;
          }
        }
        return true;
      } else {
        // For others we don't allow cancelling past slots
        return false;
      }
    }
    return true;
  }

  /**
   * Logic to whether show or hide remove payment button
   */
  function showRemovePaymentBtn(payment: any): boolean {
    // console.log("hi from show remove btn");
    // donot show button in completedUI
    if (bookingStep.value === BookingStep.CompleteOrder) {
      return false;
    }

    // donot show button for Adyen
    if (payment.method === PaymentMethods.METHOD_POS_ADYEN) {
      return false;
    }

    // donot show button for accounting of backend except for pending invoice
    if (payment.accountingId) {
      if (
        payment.state === AccountingState.Pending &&
        payment.method === PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer
      ) {
        return true;
      }
      // so this payment isn't an invoice, lets not show remove btn
      return false;
    }

    // donot show button for accounting completed before synching the booking
    if (
      payment.state === AccountingState.Completed &&
      payment.createdAt < bookingSynchingDateTime.value
    ) {
      return false;
    }

    return true;
  }

  function getStatusTranslation(pss: IQuantifiedProductSlotState) {
    if (
      pss.bookingStatus === BookingStatus.Confirmed &&
      pss.pssType === PSSType[PSSType.Retail]
    ) {
      return this.t("common.confirmed-sets");
    }
    if (
      pss.bookingStatus === BookingStatus.Canceled &&
      pss.pssType === PSSType[PSSType.Retail]
    ) {
      return this.t("common.cancelled-sets");
    }
    if (
      pss.bookingStatus === BookingStatus.Confirmed &&
      pss.pssType === PSSType[PSSType.Ticket]
    ) {
      return this.t("common.confirmed-tickets");
    }
    if (
      pss.bookingStatus === BookingStatus.Canceled &&
      pss.pssType === PSSType[PSSType.Ticket]
    ) {
      return this.t("common.cancelled-tickets");
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      showCopyCheck.value = true;
      setTimeout(() => {
        showCopyCheck.value = false;
      }, 1500);
    });
  }

  function printBill() {
    printBookingReceipt({ isBill: true });
  }

  const showPrintBill = computed(() => {
    const unsupportedCountries = ["FR", "BE"];
    if (unsupportedCountries.includes(POSConfigModule.getGroupCountryCode)) {
      return false;
    }
    if (bookingStep.value === BookingStep.CompleteOrder) {
      // Don't allow printing bill in Complete Order step
      return false;
    }

    if (selectedBooking.value?.pssList.length === 0) {
      return false;
    }
    return true;
  });

  return {
    getStatusTranslation,
    showRemoveBtn,
    showRemovePaymentBtn,
    isInThePast,
    showCancelTicketsModal,
    iconContent,
    iconAlt,
    iconStyle,
    printIcon,
    printAlt,
    t,
    bookings,
    bookingList,
    selectedBooking,
    currency,
    pssList,
    isCompletedBooking,
    isWaiverAllowed,
    bookingStep,
    BookingStep,
    isInPaymentStep,
    isInOrderStep,
    confirmedAmountsInfo,
    confirmedAmountsTotal,
    payingCurrency,
    getCustomerFullName,
    hasAppliedDiscount,
    isDiscountOnAllOrder,
    discountTotal1,
    discountTotal,
    whatIsLeftToPay,
    whatIsLeftToPayForInvoice,
    // specialWhatIsLeftToPay,
    total1,
    total,
    isLongTitle,
    isPSSConsideredInDiscount1,
    isPSSConsideredInDiscount,
    isFreeTicket,
    PSSType,
    onClickCustomers,
    // getPSSOriginalPriceValue,
    getPSSNewPriceValue,
    getTicketDiscount1,
    groupedPSSList,
    deleteUnconfirmedTickets,
    AccountingState,
    deleteTickets,
    showTicketInfo,
    formatDateTime,
    removePayment,
    deleteConfirmedAmount,
    isAccountingCompleted,
    doesSupportSplitPayment,
    balance1,
    isEditOrder,
    alreadyPaidAmount,
    refundedAmount,
    roundingTotal,
    refreshDiscount,
    calcVisibility,
    calcIcon,
    calcIconStyle,
    calcAlt,
    closeCalculator,
    // paidAccountingsSum,
    BookingStatus,
    confirmedPSSToDelete,
    openMessageBox,
    closeModal,
    selectedConfirmedPssUuids,
    selectedConfirmedPssCount,
    isAllConfirmedPssSelected,
    showCancelConfirmationModal,
    cancelConfirmationCount,
    isConfirmedPssSelected,
    toggleConfirmedPssSelection,
    toggleSelectAllConfirmedPss,
    bulkCancelSelectedTickets,
    closeCancelConfirmationModal,
    confirmTicketsCancellation,
    orderValueExcludingCancelledTickets,
    splitPayments,
    amountPaid,
    translatePaymentMethods,
    totalTax,
    PaymentMethods,
    bookingSynchingDateTime,
    hasGenericDiscountAccountings,
    groupCurrency,
    currenciesUsedInPayment,
    getPSSValue,
    getPackageTotalValue,
    bookingHasSeatingPlan,
    isRemoveButtonLoading,
    getTicketAppliedDiscountCodes,
    orderReference,
    copyToClipboard,
    showCopyCheck,
    getPssGroupDiscount,
    getTicketAppliedOffers,
    printBill,
    showPrintBill,
    showWaiverSection,
    linkedWaivers,
    linkedCount,
    isLoadingCount,
    incrementLinkedCount,
    decrementLinkedCount,
  };
}
